import React from 'react';
import { ExternalLink, CheckSquare, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

interface SyncFeature {
  label: string;
  description: string;
  supported: boolean;
}

const SYNC_FEATURES: SyncFeature[] = [
  {
    label: 'Project → ClickUp Space',
    description: 'Each BuildGravity project maps to a ClickUp space or folder.',
    supported: true,
  },
  {
    label: 'Deliverables → Tasks',
    description: 'Deliverable line items sync as ClickUp tasks with due dates.',
    supported: true,
  },
  {
    label: 'Status sync',
    description: 'Task completion in ClickUp reflects back on deliverable status.',
    supported: false,
  },
  {
    label: 'Assignee sync',
    description: 'Team member assignments flow between both platforms.',
    supported: false,
  },
];

export function ClickUpConfigPage() {
  return (
    <DashboardLayout title="ClickUp Integration">
      <div className="p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <ExternalLink className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold">ClickUp Integration</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Connect your ClickUp workspace to sync tasks and projects with BuildGravity.
            </p>
          </div>
        </div>

        {/* Connection status */}
        <div className="rounded-lg border bg-card p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            <div>
              <p className="text-sm font-medium">Not connected</p>
              <p className="text-xs text-muted-foreground">
                Authenticate with ClickUp to begin syncing.
              </p>
            </div>
          </div>
          <button
            disabled
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground opacity-60 cursor-not-allowed"
            title="ClickUp OAuth coming soon"
          >
            Connect ClickUp
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Sync features */}
        <section className="space-y-3">
          <h2 className="text-base font-medium">What Syncs</h2>
          <div className="divide-y rounded-lg border bg-card">
            {SYNC_FEATURES.map((feature) => (
              <div key={feature.label} className="flex items-start gap-3 px-4 py-3">
                <CheckSquare
                  className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                    feature.supported ? 'text-green-500' : 'text-muted-foreground'
                  }`}
                />
                <div>
                  <p className="text-sm font-medium">{feature.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{feature.description}</p>
                </div>
                {!feature.supported && (
                  <span className="ml-auto text-xs rounded-full bg-muted px-2 py-0.5 text-muted-foreground whitespace-nowrap">
                    Coming soon
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Manual sync placeholder */}
        <section className="space-y-3">
          <h2 className="text-base font-medium">Manual Sync</h2>
          <div className="rounded-lg border border-dashed p-6 flex flex-col items-center text-center gap-3">
            <RefreshCw className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Connect your workspace first</p>
            <p className="text-sm text-muted-foreground">
              Once connected, you can trigger a manual sync or configure automatic sync intervals.
            </p>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
